<template>
  <v-container v-if="getCustomer" fluid class="customer-profile-page pa-0">
    <v-alert
      v-if="getCustomer && getCustomer.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-3"
      v-model="getCustomer.alert"
      color="red"
    >
      <strong>{{ getCustomer.note }}</strong>
    </v-alert>

    <Edit
      v-if="getCustomer"
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="loadData"
      :editCustomer="getCustomer"
    />

    <v-card v-if="getCustomer" outlined class="profile-card">
      <div
        class="profile-header"
        :class="{ 'blocked-header': getCustomer.blockOrder }"
      >
        <div class="profile-title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon :color="getCustomer.blockOrder ? 'white' : '#0f172a'">
              mdi-chevron-left
            </v-icon>
          </v-btn>

          <div class="customer-avatar">
            {{ getInitial(getCustomer.name) }}
          </div>

          <div>
            <div
              class="profile-title"
              :class="{ 'white--text': getCustomer.blockOrder }"
            >
              <v-tooltip bottom v-if="getCustomer.blockOrder">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="white" small v-bind="attrs" v-on="on" class="mr-1">
                    mdi-cancel
                  </v-icon>
                </template>
                <span>Ordering is blocked</span>
              </v-tooltip>

              {{ getCustomer.name }}
            </div>

            <div
              class="profile-subtitle"
              :class="{ 'blocked-subtitle': getCustomer.blockOrder }"
            >
              Customer since {{ getCustomer.createdAt | moment("from", "now") }}
            </div>
          </div>
        </div>

        <div class="profile-actions">
          <v-chip
            v-if="
              getCustomer &&
              getCustomer.PriceTier &&
              currentBusiness &&
              currentBusiness.metadata &&
              !currentBusiness.metadata.isSingleCustomerPricing
            "
            small
            class="font-weight-bold"
            :color="getCustomer.blockOrder ? 'white' : 'red'"
            :text-color="getCustomer.blockOrder ? 'red' : 'white'"
          >
            {{ getCustomer.PriceTier.name }}
          </v-chip>

          <v-btn
            v-if="checkRightStatus(6)"
            small
            rounded
            color="primary"
            dark
            @click="newInvoice"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Invoice
          </v-btn>

          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on">
                <v-icon :color="getCustomer.blockOrder ? 'white' : '#0f172a'">
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </template>

            <v-list dense class="menu-button">
              <v-list-item
                v-if="checkRightStatus(23)"
                @click="editDialog = true"
              >
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>

              <DeleteCustomer :Customer="getCustomer" @submit="$router.go(-1)" />

              <v-list-item @click="viewOpenInvoice">
                <v-list-item-title>View Open Invoice</v-list-item-title>
              </v-list-item>

              <SyncBalance />

              <Statement :CustomerId="getCustomer.id" />

              <v-divider />

              <RemoteOrderLink :id="getCustomer.id" :customer="getCustomer" />

              <v-divider
                v-if="
                  currentBusiness.metadata &&
                  currentBusiness.metadata.serializeProduct
                "
              />

              <SearilSearch
                v-if="
                  currentBusiness.metadata &&
                  currentBusiness.metadata.serializeProduct
                "
              />

              <v-divider />

              <v-list-item @click="openProfitLoss">
                <v-list-item-title>Profit & Loss</v-list-item-title>
              </v-list-item>

              <v-list-item @click="openCustomerHistory(getCustomer)">
                <v-list-item-title>View History</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="7">
            <div class="info-grid">
              <div class="info-box">
                <div class="info-label">Contact Name</div>
                <div class="info-value">
                  {{ getCustomer.contactName || "Not Set" }}
                </div>
              </div>

              <div class="info-box">
                <div class="info-label">Phone Number</div>
                <div class="info-value">
                  <span v-if="getCustomer.phoneNumber">
                    {{ getCustomer.phoneNumber | USPhoneNumber }}
                  </span>
                  <span v-else>Not Set</span>
                </div>
              </div>

              <div class="info-box">
                <div class="info-label">Cell Number</div>
                <div class="info-value">
                  <span v-if="getCustomer.cellNumber">
                    {{ getCustomer.cellNumber | USPhoneNumber }}
                  </span>
                  <span v-else>Not Set</span>
                </div>
              </div>

              <div class="info-box">
                <div class="info-label">Email</div>
                <div class="info-value">
                  <a v-if="getCustomer.email" @click="sendEmail">
                    {{ getCustomer.email }}
                  </a>
                  <span v-else>Not Set</span>
                </div>
              </div>

              <div class="info-box">
                <div class="info-label">Tax ID</div>
                <div class="info-value">
                  {{ getCustomer.taxId || "Not Set" }}
                </div>
              </div>

              <div class="info-box">
                <div class="info-label">Customer Terms</div>
                <div class="info-value">
                  <span
                    v-if="
                      getCustomer.metadata &&
                      getCustomer.metadata.invoiceDue &&
                      getCustomer.metadata.invoiceDue.name
                    "
                  >
                    {{ getCustomer.metadata.invoiceDue.name }}
                  </span>
                  <span v-else>Not Set</span>
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <div class="side-panel">
              <div v-if="getCustomer.address" class="address-box">
                <div class="panel-label">Billing Address</div>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <a v-on="on" @click="openGoogleMap">
                      <div class="address-main">{{ getCustomer.address }}</div>
                      <div class="address-sub">
                        {{ getCustomer.city }}, {{ getCustomer.state }}
                        {{ getCustomer.zip }}
                      </div>
                    </a>
                  </template>
                  <span>Click to open google map</span>
                </v-tooltip>
              </div>

              <div v-if="getCustomer.shippingAddress" class="address-box mt-3">
                <div class="panel-label">Shipping Address</div>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <a v-on="on" @click="openShippingGoogleMap">
                      <div class="address-main">
                        {{ getCustomer.shippingAddress }}
                      </div>
                      <div class="address-sub">
                        {{ getCustomer.shippingCity }},
                        {{ getCustomer.shippingState }}
                        {{ getCustomer.shippingZip }}
                      </div>
                    </a>
                  </template>
                  <span>Click to open google map</span>
                </v-tooltip>
              </div>

              <div v-if="checkRightStatus(35)" class="balance-box mt-3">
                <div>
                  <div class="panel-label">Balance</div>
                  <div class="balance-value" :style="{ color: balanceColor }">
                    {{ getCustomer.balance | currency }}
                  </div>
                  <div class="balance-limit">
                    Limit: {{ getCustomer.balanceLimit | currency }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

<v-card outlined class="profile-tabs-card mt-3">
  <div class="tabs-header">
    <v-tabs
      v-model="active"
      slider-color="#2563eb"
      color="primary"
      show-arrows
      grow
      class="profile-tabs"
    >
      <v-tab>Dashboard</v-tab>

      <v-tab
        v-if="
          currentBusiness &&
          currentBusiness.metadata &&
          currentBusiness.metadata.isSingleCustomerPricing
        "
      >
        Custom Price
      </v-tab>

      <v-tab>Statistics</v-tab>
      <v-tab>Order</v-tab>
      <v-tab>Invoice</v-tab>
      <v-tab>Payments</v-tab>
      <v-tab>Attachment</v-tab>
      <v-tab>Communications</v-tab>
    </v-tabs>
  </div>

  <v-divider />

  <v-tabs-items
    background-color="transparent"
    v-model="active"
    touchless
    class="profile-tab-content"
  >
    <v-tab-item>
      <Dashboard />
    </v-tab-item>

    <v-tab-item
      v-if="
        currentBusiness &&
        currentBusiness.metadata &&
        currentBusiness.metadata.isSingleCustomerPricing
      "
    >
      <CustomPrice :CustomerId="getCustomer.id" />
    </v-tab-item>

    <v-tab-item>
      <Statistics />
    </v-tab-item>

    <v-tab-item>
      <OrderList class="pa-2" v-if="getCustomer" :CustomerId="getCustomer.id" />
    </v-tab-item>

    <v-tab-item>
      <InvoiceList class="pa-2"  v-if="getCustomer" :CustomerId="getCustomer.id" />
    </v-tab-item>

    <v-tab-item>
      <PaymentList class="pa-2" v-if="getCustomer" :CustomerId="getCustomer.id" />
    </v-tab-item>

    <v-tab-item>
      <AttachmentList
        v-if="getCustomer"
        TypeId="2"
        :RelationId="getCustomer.id"
      />
    </v-tab-item>

    <v-tab-item>
      <EmailHistory v-if="getCustomer" :customer-id="getCustomer.id" />
    </v-tab-item>
  </v-tabs-items>
</v-card>

    <CustomerHistoryDialog
      v-show="customerHistoryDialog"
      v-model="customerHistoryDialog"
      :customer="historyCustomer"
    />

    <DateSelector @submit="openReport" />
  </v-container>
</template>

<script>
const Dashboard = () => import("../components/Dashboard");
const CustomPrice = () =>
  import("@/modules/Customer/CustomerPrice/components/List.vue");
const Statistics = () => import("../components/Statistics");
const InvoiceList = () => import("@/modules/Invoice/components/List");
const EmailHistory = () => import("@/modules/Invoice/components/EmailHistory");
const AttachmentList = () => import("@/modules/Attachment/components/List");
const OrderList = () => import("@/modules/Order/components/List");
const PaymentList = () => import("../Payment/components/List");
const DeleteCustomer = () => import("../components/DeleteCustomer");
const RemoteOrderLink = () => import("@/components/RemoteOrderLink");
const SyncBalance = () =>
  import("@/modules/Customer/components/syncBalance.vue");
const Statement = () => import("@/modules/Customer/components/Statement");
const SearilSearch = () =>
  import("@/modules/Customer/components/ImeiSearch.vue");

import CustomerHistoryDialog from "@/modules/Customer/components/CustomerHistory.vue";
import DateSelector from "@/modules/Report/components/DateSelector";
import { mapGetters, mapActions } from "vuex";
import Hashids from "hashids";

const hashids = new Hashids();
const Edit = () => import("@/modules/Customer/components/Edit");

export default {
  name: "customer-profile",

  components: {
    Edit,
    CustomerHistoryDialog,
    DateSelector,
    RemoteOrderLink,
    SyncBalance,
    Dashboard,
    CustomPrice,
    Statement,
    InvoiceList,
    AttachmentList,
    PaymentList,
    OrderList,
    DeleteCustomer,
    Statistics,
    SearilSearch,
    EmailHistory,
  },

  data() {
    return {
      customerHistoryDialog: false,
      historyCustomer: null,
      fab: false,
      active: null,
      editDialog: false,
    };
  },

  computed: {
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("global", ["checkRightStatus", "currentBusiness"]),

    balanceColor() {
      if (
        this.getCustomer &&
        Number(this.getCustomer.balance || 0) >
          Number(this.getCustomer.balanceLimit || 0)
      ) {
        return "#dc2626";
      }

      return "#0f172a";
    },
  },

  watch: {
    $route(to, from) {
      if (to && from && to.name === from.name) {
        this.loadData();
      }
    },
  },

  created() {
    this.loadData();
    this.$events.listen("customerProfileEvent", () => this.loadData());
  },

  beforeDestroy() {
    this.$events.remove("customerProfileEvent");
  },

  methods: {
    ...mapActions("customer", ["setCustomer"]),
    ...mapActions("global", ["setPageTitle"]),
    ...mapActions("reportView", ["setDateSelector"]),

    getInitial(name) {
      if (!name) {
        return "?";
      }

      return name.charAt(0).toUpperCase();
    },

    openProfitLoss() {
      this.setDateSelector(true);
    },

    openReport(val) {
      const routeData = this.$router.resolve({
        path: `/reportView/profitAndLoss/byCustomerAndCategory?start=${val.start}&end=${val.end}&CustomerId=${this.getCustomer.id}&serializedItem=true`,
      });

      window.open(routeData.href, "_blank");
    },

    openCustomerHistory(customer) {
      if (!this.currentBusiness.metadata.historyTracking) {
        this.$swal({
          icon: "warning",
          title: "History Tracking Off",
          text: "Enable it in Settings to view change logs ($50/month).",
        });
        return;
      }

      this.historyCustomer = customer;
      this.customerHistoryDialog = true;
    },

    viewOpenInvoice() {
      const hashId = hashids.encode(this.getCustomer.id);

      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/openInvoice`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    newInvoice() {
      this.$router.push({
        path: `/invoice/create?CustomerId=${this.getCustomer.id}`,
      });
    },

    async loadData() {
      const id =
        this.$route.params.id ||
        (this.getCustomer && this.getCustomer.id ? this.getCustomer.id : null);

      if (!id) {
        return;
      }

      this.$Progress.start();
      await this.setCustomer(id);
      this.$Progress.finish();
    },

    openGoogleMap() {
      window.open(
        `https://www.google.com/maps/place/${this.getCustomer.address} ${this.getCustomer.city},${this.getCustomer.state} ${this.getCustomer.zip}`,
        "_blank"
      );
    },

    openShippingGoogleMap() {
      window.open(
        `https://www.google.com/maps/place/${this.getCustomer.shippingAddress} ${this.getCustomer.shippingCity},${this.getCustomer.shippingState} ${this.getCustomer.shippingZip}`,
        "_blank"
      );
    },

    sendEmail() {
      const routeData =
        "https://mail.google.com/mail/?view=cm&fs=1&to=" +
        this.getCustomer.email;

      window.open(routeData, "newwindow", "width=800, height=950");
    },

    closeEdit() {
      this.editDialog = false;
    },

    updated() {
      this.closeEdit();
      this.loadData();
    },
  },
};
</script>

<style scoped>
.customer-profile-page {
  background: transparent;
}

.profile-card,
.profile-tabs-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.profile-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #ffffff;
}

.blocked-header {
  background: #dc2626;
  border-bottom-color: #dc2626;
}

.profile-title-wrap {
  display: flex;
  align-items: center;
  min-width: 0;
}

.customer-avatar {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 16px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  margin-right: 12px;
  text-transform: uppercase;
}

.profile-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  text-transform: capitalize;
  line-height: 1.1;
}

.profile-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

.blocked-subtitle {
  color: #fee2e2;
}

.profile-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.info-box,
.address-box,
.balance-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.info-label,
.panel-label {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.info-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 5px;
  word-break: break-word;
}

.side-panel {
  height: 100%;
}

.address-main {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  text-transform: capitalize;
  margin-top: 5px;
}

.address-sub {
  font-size: 13px;
  color: #64748b;
  text-transform: capitalize;
  margin-top: 2px;
}

.balance-value {
  font-size: 38px;
  font-weight: 300;
  line-height: 1.1;
  margin-top: 4px;
}

.balance-limit {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

.tabs-header {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ffffff;
}

.profile-tabs {
  border-radius: 20px 20px 0 0;
}

.profile-tab-content {
  background: #ffffff;
}

::v-deep .v-tabs-bar {
  background: #ffffff;
}

@media (max-width: 960px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .profile-actions {
    width: 100%;
  }

  .profile-actions .v-btn {
    flex: 1;
  }
}

@media (max-width: 600px) {
  .profile-title {
    font-size: 18px;
  }

  .profile-actions {
    display: grid;
    grid-template-columns: 1fr auto;
  }

  .balance-value {
    font-size: 30px;
  }
}
</style>
